import { array, concat } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { next } from '@ember/runloop';
import { service } from '@ember/service';
import { cached, tracked } from '@glimmer/tracking';

import type { APIData, PopupData, Service } from 'webapp-types';
import {
	BlockDiscriminator,
	DataPerformanceSummaryType,
	PopupEvent,
	Theme,
} from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import joinClasses from 'webapp/helpers/join-classes';
import didUpdate from 'webapp/modifiers/did-update';
import BlockSettingsComponent from 'webapp/components/block/settings';
import BlockSettingsPreviewTopbarComponent from 'webapp/components/block/settings/preview-topbar';
import BlockSingleComponent from 'webapp/components/block/single';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import DataPerformanceSummaryButtonComponent from 'webapp/components/data-performance/summary-button';
import DesignerPanelSectionComponent from 'webapp/components/designer/panel-section';
import EditableH1TitleComponent from 'webapp/components/editable-h1-title';
import type { Args } from 'webapp/components/popup/inner/modal';
import PopupInnerModalComponent from 'webapp/components/popup/inner/modal';
import ReportsEditorBlockCtBgColorComponent from 'webapp/components/reports/editor/block-ct-bg-color';
import SubmitButtonComponent from 'webapp/components/submit-button';

import type { BoardPreviewRecord } from 'webapp/models';
import type { Block, BlockState, Board } from 'webapp/models-custom';
import BoardState from 'webapp/models-custom/board-state';
import type { DataPerformanceSummaryObject } from 'webapp/models-custom/data-performance-summary/block';

import type { TrackingOriginTypeForDatablockDuplicate } from 'webapp/services/model-manager/blocks';

export default class BlockPopupPreviewComponent extends PopupInnerModalComponent<PopupData.Block.Preview> {
	@service('account') declare accountService: Service.Account;
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('model-manager/blocks') declare mmBlocksService: Service.ModelManager.Blocks;
	@service('model-manager/boards') declare mmBoardsService: Service.ModelManager.Boards;
	@service('request') declare requestService: Service.Request;
	@service('request-queue') declare requestQueueService: Service.RequestQueue;
	@service('model-manager/samples') declare mmSamplesService: Service.ModelManager.Samples;
	@service('store') declare storeService: Service.Store;

	@tracked clonedModel!: Block;
	@tracked clonedModelBoardState!: BoardState;
	@tracked clonedModelBlockState!: BlockState;
	@tracked isBlockEditorShown = false;
	@tracked isClonedModelDirty = false;

	@tracked isOriginalModelReplaced = false;
	trackingOrigin = 'datablock_explorer';

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.analyticsService.event(TrackingEvent.DATABLOCK_EXPLORE, {
			origin: this.popupData.trackingOrigin,
		});
		void this.cloneModel(this.originalModel).then((block) => {
			// setting data instantly so user does not see loaders
			this.mmSamplesService.duplicateSampleForBlockCopy(this.originalModel, block);

			this.clonedModel = block;
			this.clonedModelBoardState = BoardState.createForSingleBlock(block);
			this.clonedModelBoardState.isViewOnly = false; // to make block title editable
			this.clonedModelBoardState.selectBlock(block.id); // to make block title editable
			this.clonedModelBlockState = this.clonedModelBoardState.getBlockState(block)!;
			// set themeId from previewColorConfig if available
			if (this.previewColorConfig?.themeId) {
				this.clonedModel.themeId = this.previewColorConfig.themeId;
			}
		});

		this.args.model.on(PopupEvent.CLOSING, this, () => {
			if (!this.isOriginalModelReplaced) {
				// if user tries to close too fast there is a chance that the cloned model is not created yet
				// eg. when user opens the popup before data on original block loads than closes it immediately
				void this.clonedModel?.delete();
			} else {
				this.popupData.boardState?.resetToOriginal(); // in case we are editing original block of block preview, we need to reset board to original state
			}
		});
	}

	get originalModel(): Block {
		return this.popupData.model;
	}

	@cached
	get board(): Board | null {
		let board = this.originalModel.boardObj;

		if (!board) {
			const boardId = (this.originalModel.board as BoardPreviewRecord)?.boardId;

			if (boardId) {
				board = this.mmBoardsService.findModel(String(boardId));
			}
		}

		return board;
	}

	get reportSlideFragment() {
		if (this.isOriginalModelReplaced) {
			return this.clonedModel?.reportSlideFragment;
		} else {
			return this.originalModel?.reportSlideFragment;
		}
	}

	@cached
	get isBlockOnBoard(): boolean {
		return !!this.board;
	}

	get isBlockOnReport() {
		return !!this.reportSlideFragment;
	}

	@cached
	get previewTheme(): Theme {
		if (this.isBlockOnBoard) {
			return (this.board?.bgTheme as Theme) || Theme.DARK;
		} else {
			return (this.reportSlideFragment?.blockBgTheme as Theme) || Theme.DARK;
		}
	}

	@cached
	get previewColorConfig() {
		if (this.isBlockOnBoard) {
			return this.board?.colorConfig;
		} else if (this.isBlockOnReport) {
			return this.reportSlideFragment!.blockColorConfig;
		} else {
			return undefined;
		}
	}

	@cached
	get previewColorGradient(): string {
		if (this.isBlockOnBoard) {
			return this.board?.colorGradient || '';
		} else if (this.isBlockOnReport) {
			return this.reportSlideFragment!.blockColorGradient;
		} else {
			return '';
		}
	}

	get dataPerformanceSummaryObject(): DataPerformanceSummaryObject | null {
		const objectId = this.clonedModel?.id;

		if (this.submitInProgress) {
			return null;
		}

		if (!!this.clonedModel?.showDataPerformanceSummary && objectId) {
			const summaryObj = {
				objectType: DataPerformanceSummaryType.BLOCK,
				objectId,
			} as DataPerformanceSummaryObject;

			return summaryObj;
		}

		return null;
	}

	get isEligibleForSave() {
		return this.isClonedModelDirty;
	}

	cloneModel(sourceBlock: Block): Promise<Block> {
		const params = {
			block: {
				name: sourceBlock.name,
				order: sourceBlock.order,
				posX: sourceBlock.posX,
				posY: sourceBlock.posY,
				width: sourceBlock.width,
				height: sourceBlock.height,
				discriminator: BlockDiscriminator.EXPLORER,
			},
		};

		return this.requestService
			.api<APIData.Block.RecordResponse>('POST', `/blocks/${sourceBlock.id}/clone`, params)
			.then((reply) => {
				this.storeService.pushPayload(reply);

				return this.mmBlocksService.findModel(String(reply.block.id))!;
			})
			.catch(
				this.requestService.showAllValidationErrors.bind(this.requestService),
			) as Promise<Block>;
	}

	@action
	setClonedModelIsDirty() {
		this.isClonedModelDirty = true;
	}

	@action
	checkRequestQueue() {
		if (this.requestQueueService.isRequestInProgress && !this.isClonedModelDirty) {
			this.setClonedModelIsDirty();
		}
	}

	@action
	toggleBlockEditor() {
		if (this.accountService.handleDemo()) {
			return;
		}

		this.isBlockEditorShown = !this.isBlockEditorShown;
	}

	@action
	saveAsNew() {
		if (this.accountService.handleDemo() || !this.isBlockOnBoard) {
			return;
		}

		this.clonedModel.boardId = this.board!.numericId!; // Needed for "Select one of existing Databoards" option, where selectedBoardId must be defined
		this.mmBlocksService.openDuplicatePopup({
			block: this.clonedModel,
			trackingOrigin: this.trackingOrigin as TrackingOriginTypeForDatablockDuplicate,
			onDuplicateSuccess: () => {
				this.isClonedModelDirty = false;
			},
		});
	}

	@action
	save() {
		if (this.accountService.handleDemo()) {
			return;
		}

		this.submitInProgress = true;
		this.clonedModelBoardState.isViewOnly = true; // disable block title edit

		if (this.isBlockOnBoard) {
			this.clonedModel.boardId = this.board!.numericId!;
		}

		const shouldDeleteOriginal = !this.isOriginalModelReplaced;

		// for board blocks, delete original first so the clone takes its position
		// otherwise the backend positions the clone next to the old block
		// for report blocks, save clone first and delete original after to prevent refetching old no longer existing block because of referenceId in reportSlideFragment
		const deleteFirst = shouldDeleteOriginal && this.isBlockOnBoard;

		const deleteOriginal = () => {
			return shouldDeleteOriginal
				? this.originalModel.delete().catch(() => {
						// original block is now orphaned but report/board is safe
					})
				: Promise.resolve();
		};

		const saveClone = () => {
			return this.clonedModel.save().then(() => {
				const fragment = this.reportSlideFragment;
				const newBlockId = this.clonedModel.numericId;

				// update fragment reference before deleting original
				// so the fragment never points at a deleted block
				if (fragment && newBlockId) {
					fragment.referenceId = newBlockId;
					this.isOriginalModelReplaced = true;

					return fragment.save();
				}

				this.isOriginalModelReplaced = true;
			});
		};

		const onSuccess = () => {
			this.isClonedModelDirty = false;
			this.submitInProgress = false;
			this.clonedModelBoardState.isViewOnly = false; // enable block title edit

			this.analyticsService.event(TrackingEvent.DATABLOCK_SAVED, {
				origin: this.popupData.trackingOrigin,
			});
		};

		const promise = deleteFirst
			? deleteOriginal().then(
					() =>
						new Promise<void>((resolve) =>
							next(() => saveClone().then(() => resolve())),
						),
				)
			: saveClone().then(() => deleteOriginal());

		promise
			.then(onSuccess)
			.catch(this.requestService.showAllValidationErrors.bind(this.requestService));
	}
	<template>
		<div
			class="title bgTheme-{{this.previewTheme}}"
			{{didUpdate this.checkRequestQueue this.requestQueueService.isRequestInProgress}}
		>
			<div class="title__main">
				<CommonUiIconComponent @name="MetricDetail" @size="lg" />
				{{#if this.clonedModel}}
					{{#if this.submitInProgress}}
						<h1>{{this.clonedModel.name}}</h1>
					{{else}}
						<EditableH1TitleComponent
							@model={{this.clonedModel}}
							@canEdit={{true}}
							@editableProperty="name"
						/>
					{{/if}}
				{{else}}
					<h1>{{this.originalModel.name}}</h1>
				{{/if}}
			</div>

			{{#if this.clonedModel}}
				<div class="title__actions">
					{{#if this.dataPerformanceSummaryObject}}
						<DataPerformanceSummaryButtonComponent
							@object={{this.dataPerformanceSummaryObject}}
							@trackingOrigin={{this.trackingOrigin}}
						/>
						<span>Performance summary</span>
					{{/if}}

					{{#if this.isEligibleForSave}}
						{{#if this.isBlockOnBoard}}
							<SubmitButtonComponent
								@text="Save as new"
								@noDelay={{true}}
								@onTrigger={{this.saveAsNew}}
								@isDisabled={{this.submitInProgress}}
								@theme="blue-outlined"
							/>
						{{/if}}

						<SubmitButtonComponent
							@text="Save"
							@noDelay={{true}}
							@onTrigger={{this.save}}
							@submitInProgress={{this.submitInProgress}}
						/>
					{{/if}}
				</div>
			{{/if}}
		</div>

		<div
			class={{joinClasses
				(array
					"content"
					(if this.isBlockEditorShown "content--block-editor-shown")
					(concat "bgTheme-" this.previewTheme)
				)
			}}
			style={{this.previewColorGradient}}
		>
			{{#if this.clonedModel}}
				<div
					class={{joinClasses
						(array
							"settings-bar-container"
							(if this.submitInProgress "settings-bar-container--disabled")
						)
					}}
				>
					<BlockSettingsPreviewTopbarComponent
						@blockState={{this.clonedModelBlockState}}
						@theme={{this.previewTheme}}
						@showCumulative={{true}}
						@showMasterFilter={{true}}
						@showCompare={{false}}
						@setModelIsDirty={{this.setClonedModelIsDirty}}
					/>
				</div>

				<div class="settings-panel-container">
					<div
						class={{joinClasses
							(array
								"bs-panel designer-panel"
								"designer-panel--right"
								(if this.submitInProgress "designer-panel--disabled")
							)
						}}
					>
						<div class="designer-panel__inner">
							<DesignerPanelSectionComponent
								@title="Datablock editor"
								@index={{0}}
								@isOpen={{this.isBlockEditorShown}}
								@toggle={{this.toggleBlockEditor}}
							/>

							{{#if this.isBlockEditorShown}}
								<div class="designer-panel__bg"></div>

								<div class="designer-panel__inner-content">
									<BlockSettingsComponent
										@model={{this.clonedModel}}
										@isPanelOpen={{true}}
									>
										<:additionalSettings>
											{{#if this.reportSlideFragment}}
												<ReportsEditorBlockCtBgColorComponent
													@model={{this.reportSlideFragment}}
												/>
											{{/if}}
										</:additionalSettings>
									</BlockSettingsComponent>
								</div>
							{{/if}}
						</div>
					</div>
				</div>

				<div class="chart-container">
					<BlockSingleComponent
						@boardState={{this.clonedModelBoardState}}
						@colorConfig={{this.previewColorConfig}}
					/>
				</div>
			{{/if}}
		</div>
	</template>
}
